import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createParser,
  createSerializer,
  parseAsArrayOf,
  parseAsString,
  parseAsStringLiteral,
  useQueryStates,
} from 'nuqs';
import type { SortBy } from '../services/api/models/SortBy';
import { SORTBY_VALUES } from '../services/api/models/SortBy';
import type { SortDirection } from '../services/api/models/SortDirection';
import {
  encodeHotboxDateDeltas,
  HOTBOX_DATE_DELTA_NONE,
  resolveHotboxDateRange,
  type HotboxDateDeltaParam,
} from '../utils/hotbox';

export const HOTBOX_SCOPES = ['clinician', 'qa'] as const;
export type HotboxScope = (typeof HOTBOX_SCOPES)[number];

export const HOTBOX_QA_SCOPES = ['all', 'my'] as const;
export type HotboxQAScope = (typeof HOTBOX_QA_SCOPES)[number];

export const HOTBOX_TASK_TYPES = [
  'visit',
  'order',
  'patient_communication',
  'misc_task',
] as const;
export type HotboxTaskType = (typeof HOTBOX_TASK_TYPES)[number];

export const HOTBOX_STATUSES = [
  'not_started',
  'saved',
  'returned_for_review',
  'pending_clinician_signature',
  'submitted_with_signature',
  'submitted',
  // Shared by clinician and QA scopes (main); still cleared on scope switch
  // with the rest of the status filter, matching Hotbox page behavior.
  'reopened',
] as const;
export type HotboxStatus = (typeof HOTBOX_STATUSES)[number];

export const HOTBOX_EVV_FILTERS = ['all', 'captured', 'not_captured'] as const;
export type HotboxEvvFilter = (typeof HOTBOX_EVV_FILTERS)[number];

// "roger": Show only Roger EMR tasks. The page ignores it while the filter is
// not offered (useRogerTasksFilter).
export const HOTBOX_CUSTODY_FILTERS = ['all', 'roger'] as const;
export type HotboxCustodyFilter = (typeof HOTBOX_CUSTODY_FILTERS)[number];

const DEFAULT_TASK_TYPES: HotboxTaskType[] = ['visit'];
const DEFAULT_SORT: SortBy = 'visit_date';
const DEFAULT_SORT_DIRECTION: SortDirection = 'desc';

const parseAsHotboxDateDelta = createParser({
  parse: (value: string): HotboxDateDeltaParam | null => {
    if (value === HOTBOX_DATE_DELTA_NONE) return HOTBOX_DATE_DELTA_NONE;
    if (!/^-?\d+$/.test(value)) return null;
    return Number(value);
  },
  serialize: (value: HotboxDateDeltaParam) =>
    value === HOTBOX_DATE_DELTA_NONE ? HOTBOX_DATE_DELTA_NONE : String(value),
  eq: (a, b) => a === b,
});

// Non-PHI filters only. Patient-name search (`q`) and PDX diagnosis filters
// stay in component state so they never land in browser history, bookmarks,
// referrers, or URL logs.
const parsers = {
  scope: parseAsStringLiteral(HOTBOX_SCOPES).withDefault('clinician'),
  qa: parseAsStringLiteral(HOTBOX_QA_SCOPES).withDefault('all'),
  task_types: parseAsArrayOf(
    parseAsStringLiteral(HOTBOX_TASK_TYPES)
  ).withDefault(DEFAULT_TASK_TYPES),
  statuses: parseAsArrayOf(parseAsStringLiteral(HOTBOX_STATUSES)).withDefault(
    []
  ),
  visit_types: parseAsArrayOf(parseAsString).withDefault([]),
  evv: parseAsStringLiteral(HOTBOX_EVV_FILTERS).withDefault('all'),
  custody: parseAsStringLiteral(HOTBOX_CUSTODY_FILTERS).withDefault('all'),
  // Day offsets from business today so bookmarks stay relative ("last 30
  // days"), not a fixed calendar window that goes stale overnight.
  df: parseAsHotboxDateDelta,
  dt: parseAsHotboxDateDelta,
  sort: parseAsStringLiteral(SORTBY_VALUES).withDefault(DEFAULT_SORT),
  dir: parseAsStringLiteral(['asc', 'desc'] as const).withDefault(
    DEFAULT_SORT_DIRECTION
  ),
};

const HOTBOX_QUERY_KEYS = Object.keys(parsers) as (keyof typeof parsers)[];

/** sessionStorage key for the last non-default Hotbox query string. */
export const HOTBOX_QUERY_STORAGE_KEY = 'roger.hotboxQuery';

const serializeHotboxQuery = createSerializer(parsers, {
  clearOnDefault: true,
});

type Updater<T> = T | ((current: T) => T);

function resolveUpdater<T>(update: Updater<T>, current: T): T {
  return typeof update === 'function'
    ? (update as (current: T) => T)(current)
    : update;
}

function hotboxKeysPresentInSearch(search: string): boolean {
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search
  );
  return HOTBOX_QUERY_KEYS.some(key => params.has(key));
}

function readStashedHotboxQuery(): string | null {
  try {
    return sessionStorage.getItem(HOTBOX_QUERY_STORAGE_KEY);
  } catch {
    return null;
  }
}

function stashHotboxQuery(query: string): void {
  try {
    sessionStorage.setItem(HOTBOX_QUERY_STORAGE_KEY, query);
  } catch {
    // Private mode / storage disabled — URL persistence still works.
  }
}

function clearStashedHotboxQuery(): void {
  try {
    sessionStorage.removeItem(HOTBOX_QUERY_STORAGE_KEY);
  } catch {
    // Private mode / storage disabled — URL persistence still works.
  }
}

function parseStashedHotboxQuery(stored: string): Partial<{
  [K in keyof typeof parsers]: ReturnType<(typeof parsers)[K]['parse']>;
}> | null {
  const params = new URLSearchParams(stored);
  const updates: Record<string, unknown> = {};
  let found = false;
  for (const key of HOTBOX_QUERY_KEYS) {
    const raw = params.get(key);
    if (raw === null) continue;
    const parsed = parsers[key].parse(raw);
    if (parsed === null) continue;
    updates[key] = parsed;
    found = true;
  }
  return found ? updates : null;
}

/**
 * Hotbox filter / sort state synced to the URL via nuqs.
 *
 * Completing a visit navigates away and remounts `/hotbox` on return; keeping
 * selections in the query string is what preserves them across that cycle.
 * Dates are stored as day deltas from business today (`df` / `dt`), not ISO
 * dates, so a bookmark remains a rolling window.
 *
 * The sidebar link is bare `/hotbox` and a visit's back button often lands on
 * the patient page, so the last non-default query is also stashed in
 * sessionStorage and restored when `/hotbox` opens with no Hotbox params.
 * Resetting filters back to defaults clears that stash so an explicit clear
 * is not undone by the next bare-link open.
 *
 * Patient-name search and PDX filters are intentionally *not* URL-backed —
 * they can contain PHI and must remain transient.
 */
export function useHotboxParams() {
  const [url, setUrl] = useQueryStates(parsers, {
    history: 'replace',
    clearOnDefault: true,
  });
  const didRestoreRef = useRef(false);
  // Tracks whether this mount has ever held a non-default query, so an
  // explicit reset can clear the stash without wiping it on the initial
  // bare `/hotbox` render (before restore runs).
  const hadNonDefaultQueryRef = useRef(false);

  const [searchTerm, setSearchTermState] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pdxFilter, setPdxFilterState] = useState<string[]>([]);
  const [pdxMissing, setPdxMissingState] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Sidebar → Hotbox is bare `/hotbox`; restore the last query for this tab.
  useEffect(() => {
    if (didRestoreRef.current) return;
    didRestoreRef.current = true;
    if (hotboxKeysPresentInSearch(window.location.search)) return;
    const stored = readStashedHotboxQuery();
    if (!stored) return;
    const updates = parseStashedHotboxQuery(stored);
    if (!updates) return;
    void setUrl(updates);
  }, [setUrl]);

  // Keep sessionStorage aligned with the live Hotbox query. Non-default
  // selections are stashed for bare-link restore; an explicit reset back to
  // defaults clears the stash so filters do not resurrect on the next visit.
  useEffect(() => {
    const serialized = serializeHotboxQuery(url);
    const query = serialized.startsWith('?') ? serialized.slice(1) : serialized;
    if (query) {
      hadNonDefaultQueryRef.current = true;
      stashHotboxQuery(query);
      return;
    }
    if (hadNonDefaultQueryRef.current) {
      clearStashedHotboxQuery();
      hadNonDefaultQueryRef.current = false;
    }
  }, [url]);

  const { from: dateFrom, to: dateTo } = useMemo(
    () => resolveHotboxDateRange(url.df, url.dt),
    [url.df, url.dt]
  );

  const setActiveScope = useCallback(
    (scope: HotboxScope) =>
      // The two scopes share only Reopened, so a selection made in one mostly
      // means nothing in the other. Clear statuses on a real scope change so a
      // stale pick does not silently empty the queue; re-clicking the active
      // tab must not wipe a status filter.
      // Returning null from the updater would clear every key (nuqs nullMap);
      // {} is a no-op so re-selecting the active tab does not touch the URL.
      setUrl(old => (scope === old.scope ? {} : { scope, statuses: [] })),
    [setUrl]
  );

  const setQAScope = useCallback(
    (qa: HotboxQAScope) => setUrl({ qa }),
    [setUrl]
  );

  const setTaskTypes = useCallback(
    (update: Updater<HotboxTaskType[]>) =>
      setUrl(old => ({
        task_types: resolveUpdater(update, old.task_types),
      })),
    [setUrl]
  );

  const setStatusFilter = useCallback(
    (update: Updater<HotboxStatus[]>) =>
      setUrl(old => ({
        statuses: resolveUpdater(update, old.statuses),
      })),
    [setUrl]
  );

  const setTypeFilter = useCallback(
    (update: Updater<string[]>) =>
      setUrl(old => ({
        visit_types: resolveUpdater(update, old.visit_types),
      })),
    [setUrl]
  );

  const setEvvFilter = useCallback(
    (update: Updater<HotboxEvvFilter>) =>
      setUrl(old => ({
        evv: resolveUpdater(update, old.evv),
      })),
    [setUrl]
  );

  const setCustodyFilter = useCallback(
    (custody: HotboxCustodyFilter) => setUrl({ custody }),
    [setUrl]
  );

  const setPdxFilter = useCallback((update: Updater<string[]>) => {
    setPdxFilterState(current => resolveUpdater(update, current));
  }, []);

  const setPdxMissing = useCallback((update: Updater<boolean>) => {
    setPdxMissingState(current => resolveUpdater(update, current));
  }, []);

  const setSearchTerm = useCallback((value: string) => {
    setSearchTermState(value);
  }, []);

  const setDateFrom = useCallback(
    (from: Date | undefined) => {
      const encoded = encodeHotboxDateDeltas(from, dateTo);
      return setUrl({ df: encoded.fromDelta, dt: encoded.toDelta });
    },
    [dateTo, setUrl]
  );

  const setDateTo = useCallback(
    (to: Date | undefined) => {
      const encoded = encodeHotboxDateDeltas(dateFrom, to);
      return setUrl({ df: encoded.fromDelta, dt: encoded.toDelta });
    },
    [dateFrom, setUrl]
  );

  const setSort = useCallback(
    (sort: SortBy, dir: SortDirection) => setUrl({ sort, dir }),
    [setUrl]
  );

  return {
    activeScope: url.scope,
    setActiveScope,
    qaScope: url.qa,
    setQAScope,
    taskTypes: url.task_types,
    setTaskTypes,
    statusFilter: url.statuses,
    setStatusFilter,
    typeFilter: url.visit_types,
    setTypeFilter,
    evvFilter: url.evv,
    setEvvFilter,
    custodyFilter: url.custody,
    setCustodyFilter,
    pdxFilter,
    setPdxFilter,
    pdxMissing,
    setPdxMissing,
    searchTerm,
    setSearchTerm,
    debouncedSearch,
    sortField: url.sort,
    sortDirection: url.dir,
    setSort,
    dateFrom,
    dateTo,
    setDateFrom,
    setDateTo,
  };
}

export type UseHotboxParamsResult = ReturnType<typeof useHotboxParams>;
